/* eslint-disable prettier/prettier */

import { useState, useEffect } from 'react';

import Accordion from './Accordion';
import LayoutContainer from './LayoutContainer';

import icCup from '@/assets/ic_cup.svg';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { AccordionItem, FaqSectionProps } from '@/types/faq';

const SpecialTournaments = ({ state, title }: FaqSectionProps) => {
  const { isMobile } = useIsDesktop();
  const [activeAccordion, setActiveAccordion] = useState<string | null>(
    state?.accordionId || null
  );

  // Actualizar el acordeón activo cuando cambia el state
  useEffect(() => {
    if (state?.accordionId) {
      setActiveAccordion(state.accordionId);
    }
  }, [state]);

  // Manejador para alternar acordeones
  const toggleAccordion = (id: string) => {
    setActiveAccordion(activeAccordion === id ? null : id);
  };

  // Datos para los acordeones
  const accordionItems: AccordionItem[] = [
    {
      id: 'especiales-futbol5-como-armo-equipo',
      title: 'Fútbol 5: ¿Cómo armo mi equipo?',
      content: (
        <div className="text-sm text-gray-600">
          <p>
            Fútbol 5 es una competencia especial dentro de Gran DT. Para
            participar, es necesario que armes un equipo con un arquero y cuatro
            jugadores de campo, sin restricción de puesto para estos. No hay
            tácticas ni límite de presupuesto, por lo que podrás crear un equipo
            con un arquero y cuatro delanteros, o bien un arquero y cuatro
            volantes, o cualquier combinación que creas mejor.
          </p>
          <br />
          <p>
            Una vez que hayas elegido a tus 5 futbolistas, deberás seleccionar
            un capitán, que duplicará la calificación Clarín y un{' '}
            <strong>Jugador Asegurado.</strong>
          </p>
        </div>
      ),
    },
    {
      id: 'especiales-futbol5-que-es-asegurado',
      title: 'Fútbol 5: ¿Qué es el Jugador asegurado?',
      content: (
        <div className="text-sm text-gray-600">
          <p>
            El jugador asegurado es un futbolista dentro de tu equipo de Fútbol
            5 que, si por algún motivo no puntúa en la fecha (porque no juega o
            juega menos de 20 minutos), igual suma 5 puntos. Siempre deberás
            elegir un futbolista entre tus 5 titulares para que sea el{' '}
            <strong>Jugador Asegurado.</strong>
          </p>
        </div>
      ),
    },
    {
      id: 'especiales-torneo-amigos-que-son',
      title: '¿Qué son los Torneos de Amigos?',
      content: (
        <div className="text-sm text-gray-600">
          <p>
            Los Torneos de Amigos son competencias privadas entre conocidos.
            Podés crearlo, ponerle un nombre, elegir una fecha de inicio e
            invitar a participar a tus amigos. El administrador de cada Torneo,
            que es quien los crea, autoriza a los distintos técnicos virtuales a
            ser parte de la competencia.
          </p>
          <br />
          <p>
            Una vez que el administrador te acepte, competirás fecha a fecha
            contra todos los técnicos del Torneo de Amigos. Podés participar con
            tus equipos de Primera y de Fútbol 5.
          </p>
        </div>
      ),
    },
    {
      id: 'especiales-torneo-amigos-diferencia-clasicos-vs-premios',
      title:
        '¿En qué se diferencian los Torneos de Amigos Clásicos de los Torneos de Amigos por Premios?',
      content: (
        <div className="text-sm text-gray-600">
          <p>
            Además de los Torneos de Amigos Clásicos, donde creás un Torneo y
            competís cada fecha contra tus conocidos, existen los Torneos de
            Amigos por Premios. Estos son mini torneos de 20 participantes con
            premios en efectivo para el campeón de cada uno. Hay distintos tipos
            de Torneos y para participar de cada uno deberás ingresar un pin
            especial. Hay torneos con $75.000 para el campeón, otros con
            $150.000 para el mejor y un tercer grupo con $250.000 para el
            ganador.
          </p>
        </div>
      ),
    },
    {
      id: 'especiales-que-son-miniligas',
      title: '¿Qué son las Miniligas y cómo se participa?',
      content: (
        <div className="text-sm text-gray-600">
          <p>
            Las Miniligas son competencias especiales dentro del juego de Gran
            DT. Cada una está compuesta por 12 participantes que tienen armado
            su equipo de Primera. Los equipos que conforman cada Miniliga
            competirán entre sí en desafíos mano a mano, determinados por un
            fixture interno de la Miniliga en cuestión. Los puntos se computarán
            de la siguiente manera:
          </p>
          <br />
          <ul className="list-disc pl-6 marker:text-black">
            <li>
              Desafío ganado (más puntos que el adversario) suma 3 (tres)
              puntos.
            </li>
            <li>
              Desafío perdido (menos puntos que el adversario) suma 0 (cero)
              puntos.
            </li>
            <li>
              Desafío empatado (igual puntos que el adversario) suma 1 (un)
              punto.
            </li>
          </ul>
          <br />
          <p>
            Así, en cada Miniliga se generará un Ranking interno. El ganador de
            cada una pasará a la Gran Final y competirán entre sí en una fecha
            determinada por premios en efectivo.
          </p>
          <br />
          <p>Hay dos formas de participar de las Miniligas:</p>
          <br />
          <ul className="list-disc pl-6 marker:text-black">
            <li>
              De manera automática: En la fecha de inicio de las Miniligas, Gran
              DT generará Miniligas automáticas con 12 participantes elegidos al
              azar.
            </li>
            <li>
              De manera no automática: Los participantes podrán crear su
              Miniliga, ponerle un nombre e invitar a 9 amigos a competir en
              ella. Todas las Miniligas que cuenten con 12 participantes antes
              del inicio de la competencia quedarán activas.
            </li>
          </ul>
        </div>
      ),
    },
    {
      id: 'especiales-que-es-torneo-escuelas',
      title: '¿Qué es el Torneo de Escuelas Primarias y cómo participar?',
      content: (
        <div className="text-sm text-gray-600">
          <p>
            El Torneo de Escuelas Primarias es una competencia especial de la
            que participan todos los DT&apos;s que sean alumnos regulares de una
            escuela primaria y que hayan completado en sus Datos personales los
            campos de provincia, partido, localidad y nombre de la Escuela a la
            que asisten y de la cual son alumnos regulares.
          </p>
          <br />
          <p>
            Además de la competencia de todos los participantes del Torneo de
            Escuelas Primarias, en paralelo existirá un Ranking de las Escuelas,
            que mostrará los puntos de cada una, donde la mejor posición será
            ocupada por la institución con mejor promedio de puntos. El promedio
            de puntos se obtiene sumando la cantidad total de puntos dividida
            por la cantidad de participantes que declaren ser alumnos de la
            institución. Para que una escuela pueda formar parte del Ranking,
            deberá contar con un mínimo de 2 participantes del Torneo de
            Escuelas.
          </p>
        </div>
      ),
    },
    {
      id: 'especiales-que-es-torneo-escuelas-secundarias',
      title: '¿Qué es el Torneo de Escuelas Secundarias y cómo participar?',
      content: (
        <div className="text-sm text-gray-600">
          <p>
            El Torneo de Escuelas Secundarias es una competencia especial de la
            que participan todos los DT&apos;s que sean alumnos regulares de una
            escuela secundaria y que hayan completado en sus Datos personales
            los campos de provincia, partido, localidad y nombre de la Escuela a
            la que asisten y de la cual son alumnos regulares.
          </p>
          <br />
          <p>
            Además de la competencia de todos los participantes del Torneo de
            Escuelas Secundarias, en paralelo existirá un Ranking de las
            Escuelas, que mostrará los puntos de cada una, donde la mejor
            posición será ocupada por la institución con mejor promedio de
            puntos. El promedio de puntos se obtiene sumando la cantidad total
            de puntos dividida por la cantidad de participantes que declaren ser
            alumnos de la institución. Para que una escuela pueda formar parte
            del Ranking, deberá contar con un mínimo de 2 participantes del
            Torneo de Escuelas.
          </p>
        </div>
      ),
    },
    {
      id: 'especiales-que-son-las-fechas-de-oro',
      title: '¿Qué son las Fechas de Oro y cómo participar?',
      content: (
        <div className="text-sm text-gray-600">
          <p>
            La <strong>Fecha de Oro</strong> es una competencia especial dentro
            de Gran DT, de la que participan 20 DT&apos;s en una fecha a
            definir, por $500.000.
          </p>
          <br />
          <p>
            Para anotarse, los DT&apos;s deben obtener su pin especial de
            $50.000 desde la sección Mis Pines. Cada torneo tendrá 20
            participantes que competirán entre sí con sus equipos de Primera
            División. Podrá haber más de un torneo &rdquo;Fecha de Oro&rdquo;
            por fecha, pudiendo participar solo 20 DT&apos;s en cada torneo.
          </p>
          <br />
          <p>
            El ganador es el técnico que más puntos suma en la fecha con su
            equipo de Primera División.
          </p>
        </div>
      ),
    },
    {
      id: 'especiales-que-son-los-desafios',
      title: '¿Qué son los Desafíos 1 a 1 y cómo participar?',
      content: (
        <div className="text-sm text-gray-600">
          <p>
            Con tu equipo de Primera, podés desafiar a tus amigos en mano a
            mano. Podrás desafiar a cuantos amigos quieras y lo podrás hacer
            todas las fechas, desde que esté activa la competencia.
          </p>
        </div>
      ),
    },
    {
      id: 'especiales-que-es-la-copa-de-amigos',
      title: '¿Qué es la Copa de Amigos y cómo participar?',
      content: (
        <div className="text-sm text-gray-600">
          <p>
            La Copa de Amigos es una competencia especial en la que los
            participantes que tengan armado su equipo de Primera, podrán armar
            un &ldquo;equipo&rdquo; con 3 amigos más y competir contra otros
            &ldquo;equipos&rdquo;.
          </p>
          <br />
          <p>
            Cada equipo estará formado por cuatro DT&apos;s. Uno será el
            administrador, que es quien crea el equipo, le pone un nombre e
            invita a sus amigos a participar. El equipo quedará activo una vez
            que se complete con los 4 participantes y competirá contra otros
            equipos.
          </p>
        </div>
      ),
    },
  ];

  return (
    <LayoutContainer>
      <div className="flex gap-x-3 items-center content-center mb-8">
        {isMobile && (
          <img
            alt="Gran DT - logo calendario"
            className="w-6 h-6"
            src={icCup}
          />
        )}
        <span className={`text-lg text-[#333333] font-bold`}>{title}</span>
      </div>

      {accordionItems.map((item) => (
        <Accordion
          key={item.id}
          isOpen={activeAccordion === item.id}
          title={item.title}
          onToggle={() => toggleAccordion(item.id)}
        >
          {item.content}
        </Accordion>
      ))}
    </LayoutContainer>
  );
};

export default SpecialTournaments;
